import { MoDataStore } from 'ui-common';
import { AgentStudioStoreMockPlugin } from './mocks/AgentStudioStoreMockPlugin';

export class AgentStudioStore extends MoDataStore {
  static defaultConfig: any = {
    mockPlugin: AgentStudioStoreMockPlugin,
    api: {
      service: 'templates',
      list: 'getTemplates',
      get: 'getTemplate',
      add: 'createTemplate',
      update: 'updateTemplate',
      remove: 'deleteTemplate',
    },
  };

  constructor(config?: any) {
    super(config);
    this.alwaysRefreshData = true;
  }

  // refer to store data object as templates to increase readability during data manipulation
  override getMeta() {
    const meta = this.cloneProps(['config', 'data', 'loaded', 'alwaysRefreshData', 'idProp']);

    return {
      ...meta,
      templates: meta.data,
    };
  }

  /**
   * Hard refresh the data when needed, targeting lower-level consumers such as Data Stores and Services.
   * For example, the store should perform a hard refresh when an agent's data sources are added, updated, or removed.
   */
  refresh() {
    this.loaded = false;

    return this.load();
  }

  async getComponents() {
    let response;
    try {
      response = await this.callApiMethod({
        service: 'components',
        name: 'getComponents'
      }, {})
        .then((data) => data)
        .catch((err) => `Error ${err}`);
    } catch (error) {
      return error;
    }
    return response;
  }

  async getComponentType(type: string) {
    const pathParams = { pathParams: { type } };
    return await this.callApiMethod({
      service: 'components',
      name: 'getComponentType'
    }, {}, pathParams);
  }

  // SOLUTION PACKS
  async getAISolutionPacks() {
    let response;
    try {
      response = await this.callApiMethod({
        service: 'introspection',
        name: 'getAISolutionPacks'
      }, {})
        .then((data) => data)
        .catch((err) => `Error ${err}`);
    } catch (error) {
      return error;
    }
    return response;
  }

  // DATASTORES
  async getDatastores() {
    return await this.callApiMethod({
      service: 'datastores',
      name: 'getDatastores'
    }, {});
  }

  async getDatastore(id: string) {
    const pathParams = { pathParams: { id } };
    return await this.callApiMethod({
      service: 'datastores',
      name: 'getDatastore'
    }, {}, pathParams);
  }

  async createDatastore(data) {
    return await this.callApiMethod({
      service: 'datastores',
      name: 'createDatastore',
      data
    });
  }

  async updateDatastore(id: string, data) {
    const pathParams = { pathParams: { id } };
    return await this.callApiMethod({
      service: 'datastores',
      name: 'updateDatastore',
      data
    }, {}, pathParams);
  }

  async deleteDatastore(id: string) {
    const pathParams = { pathParams: { id } };
    return await this.callApiMethod({
      service: 'datastores',
      name: 'deleteDatastore'
    }, {}, pathParams);
  }

  // INDEX TARGETS
  async getIndexTargets(customerId: string) {
    const pathParams = { pathParams: { customerId } };
    return await this.callApiMethod({
      service: 'index-targets',
      name: 'getIndexTargets'
    }, {}, pathParams);
  }

  async getIndexTarget(customerId: string, id: string) {
    const pathParams = { pathParams: { customerId, id } };
    return await this.callApiMethod({
      service: 'index-targets',
      name: 'getIndexTarget'
    }, {}, pathParams);
  }

  async createIndexTarget(customerId: string, data) {
    const pathParams = { pathParams: { customerId } };
    return await this.callApiMethod({
      service: 'index-targets',
      name: 'createIndexTarget',
      data
    }, {}, pathParams);
  }

  async updateIndexTarget(customerId: string, id: string, data: any) {
    const pathParams = { pathParams: { customerId, id } };
    return await this.callApiMethod({
      service: 'index-targets',
      name: 'updateIndexTarget',
      data
    }, {}, pathParams);
  }

  async deleteIndexTarget(customerId: string, id: string) {
    const pathParams = { pathParams: { customerId, id } };
    return await this.callApiMethod({
      service: 'index-targets',
      name: 'deleteIndexTarget'
    }, {}, pathParams);
  }

  async getIndexTargetStats(customerId: string, indexTargetId: string) {
    const pathParams = { pathParams: { customerId, id: indexTargetId } };
    return await this.callApiMethod({
      service: 'stats',
      name: 'getIndexTargetStats'
    }, {}, pathParams);
  }

  // AGENT CONFIGURATIONS
  async getAgentConfigurations() {
    return await this.callApiMethod({
      service: 'agentConfigurations',
      name: 'getAgentConfigurations'
    }, {});
  }

  async updateAgentConfiguration(id: string, data) {
    const pathParams = { pathParams: { id } };
    return await this.callApiMethod({
      service: 'agentConfigurations',
      name: 'updateAgentConfiguration',
      data
    }, {}, pathParams);
  }

  async getAgentConfiguration(id: string) {
    const pathParams = { pathParams: { id } };
    return await this.callApiMethod({
      service: 'agentConfigurations',
      name: 'getAgentConfiguration'
    }, {}, pathParams);
  }

  async createAgentConfiguration(data) {
    return await this.callApiMethod({
      service: 'agentConfigurations',
      name: 'createAgentConfiguration',
      data
    });
  }

  async deleteAgentConfiguration(id: string) {
    const pathParams = { pathParams: { id } };
    return await this.callApiMethod({
      service: 'agentConfigurations',
      name: 'deleteAgentConfiguration'
    }, {}, pathParams);
  }
  
  hasDuplicateTemplateName(id: string, name: string) {
    return this.data.find(template => template.name === name && template.id !== id);
  }

  async updateItem(data, pathParams?) {
    // Make the API call directly to get the response
    const response = await this.callApiMethod({
      service: 'templates',
      name: 'updateTemplate',
      data,
    }, {}, pathParams ?? { pathParams: { id: data.id }});
    
    // Manually refresh the store and wait for it to complete
    // This ensures the store's data property is updated
    await this.load();
    
    // Fire the update event that the parent would have fired
    // This ensures subscribers are notified
    this.fireEvent('update', response);
    
    // Return the actual API response
    return response;
  }

  async getNewestTemplateVersion(id: string) {
    const pathParams = { pathParams: { id } };
    return await this.callApiMethod({
      service: 'templates',
      name: 'getNewestTemplateVersion'
    }, {}, pathParams);
  }

  async getTemplateHistory(id: string) {
    const pathParams = { pathParams: { id } };
    return await this.callApiMethod({
      service: 'templates',
      name: 'getTemplateHistory'
    }, {}, pathParams);
  }
}
